/**
 * ImpactSystem — Unified "game feel" choreographer.
 *
 * Every impactful moment in the game (ball pop, boss hit, heart loss, kill blow,
 * combo milestone) flows through this system so that:
 *   - Hit-stop, camera kick, flash, shockwave, and slow-mo all fire consistently.
 *   - Intensity scales cleanly from a single number (0..1).
 *   - Time-scale restoration is safe under rapid fire (ref-counted freeze).
 *   - Camera push-pull stacks additively so multi-hit bursts feel heavier.
 *
 * Usage:
 *   ImpactSystem.get(scene).pop(x, y, 0.6, 0xFF5566);    // medium pop
 *   ImpactSystem.get(scene).boss(x, y);                  // boss phase
 *   ImpactSystem.get(scene).finalBlow(x, y);             // last ball of stage
 *   ImpactSystem.get(scene).timeSlice(300, 0.3);         // 300ms @ 30% speed
 *
 * All methods are NO-OP safe — call them from anywhere, even if the scene has
 * been torn down. The system gates itself on the screen-shake toggle.
 */

import Phaser from 'phaser';
import { screenShakeState } from '@/config/constants';

// ─── Intensity presets ───────────────────────────────────────────────────────

export interface ImpactPreset {
  freezeMs: number;           // hit-stop duration (real time, ignores timeScale)
  shakeDuration: number;      // camera shake length
  shakeIntensity: number;     // 0..0.03 typical
  flashAlpha: number;         // white flash alpha (0..1)
  flashColor: number;         // hex color
  zoomPunch: number;          // additive zoom kick (e.g. 0.02 = 102%)
  zoomRecoverMs: number;      // how long to spring back
  shockwaveRings: number;     // how many expanding rings
  shockwaveScale: number;     // ring max scale multiplier
  particleCount: number;      // extra debris particles
  barrel?: number;            // if set, applies post-fx barrel distort
  chromatic?: number;         // if set, splits RGB channels briefly
}

export const IMPACT_PRESETS = {
  TINY: {
    freezeMs: 20, shakeDuration: 60, shakeIntensity: 0.0008,
    flashAlpha: 0.35, flashColor: 0xFFFFFF,
    zoomPunch: 0.005, zoomRecoverMs: 160,
    shockwaveRings: 1, shockwaveScale: 3.0,
    particleCount: 4,
  } as ImpactPreset,
  SMALL: {
    freezeMs: 35, shakeDuration: 80, shakeIntensity: 0.0015,
    flashAlpha: 0.45, flashColor: 0xFFFFFF,
    zoomPunch: 0.01, zoomRecoverMs: 200,
    shockwaveRings: 1, shockwaveScale: 4.0,
    particleCount: 8,
  } as ImpactPreset,
  MEDIUM: {
    freezeMs: 55, shakeDuration: 120, shakeIntensity: 0.0025,
    flashAlpha: 0.6, flashColor: 0xFFFFFF,
    zoomPunch: 0.018, zoomRecoverMs: 260,
    shockwaveRings: 2, shockwaveScale: 5.0,
    particleCount: 14,
  } as ImpactPreset,
  LARGE: {
    freezeMs: 90, shakeDuration: 160, shakeIntensity: 0.004,
    flashAlpha: 0.75, flashColor: 0xFFFFFF,
    zoomPunch: 0.028, zoomRecoverMs: 340,
    shockwaveRings: 3, shockwaveScale: 6.5,
    particleCount: 24,
    barrel: 0.15,
  } as ImpactPreset,
  HUGE: {
    freezeMs: 130, shakeDuration: 220, shakeIntensity: 0.006,
    flashAlpha: 0.9, flashColor: 0xFFFFFF,
    zoomPunch: 0.04, zoomRecoverMs: 420,
    shockwaveRings: 3, shockwaveScale: 8.0,
    particleCount: 40,
    barrel: 0.25,
    chromatic: 4,
  } as ImpactPreset,
  COSMIC: {
    freezeMs: 180, shakeDuration: 320, shakeIntensity: 0.009,
    flashAlpha: 1.0, flashColor: 0xFFD700,
    zoomPunch: 0.06, zoomRecoverMs: 520,
    shockwaveRings: 4, shockwaveScale: 10,
    particleCount: 60,
    barrel: 0.35,
    chromatic: 6,
  } as ImpactPreset,
} as const;

export type ImpactPresetName = keyof typeof IMPACT_PRESETS;

// ─── System ──────────────────────────────────────────────────────────────────

/**
 * One ImpactSystem per scene. Resets itself automatically on scene shutdown.
 */
export class ImpactSystem {
  private static instances: WeakMap<Phaser.Scene, ImpactSystem> = new WeakMap();

  /**
   * Get or create the system for a scene.
   */
  static get(scene: Phaser.Scene): ImpactSystem {
    let inst = this.instances.get(scene);
    if (!inst) {
      inst = new ImpactSystem(scene);
      this.instances.set(scene, inst);
      scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        inst!.destroy();
        this.instances.delete(scene);
      });
    }
    return inst;
  }

  /**
   * Map a raw ball radius to a preset. Used by GameScene ball pops.
   */
  static presetForRadius(radius: number): ImpactPresetName {
    if (radius >= 140) return 'HUGE';
    if (radius >= 100) return 'LARGE';
    if (radius >= 60) return 'MEDIUM';
    if (radius >= 30) return 'SMALL';
    return 'TINY';
  }

  private scene: Phaser.Scene;
  private freezeRefCount = 0;
  private freezeRestoreTimer?: number;
  private baseTimeScale = 1;
  private cameraZoomStack = 0;
  private zoomBaseScale = 1;
  private barrelFX?: Phaser.Filters.Barrel;
  private barrelTween?: Phaser.Tweens.Tween;
  private shockwaveTexKey = 'impact_shockwave';
  private destroyed = false;
  // Wall-clock timeouts owned by this system (timeSlice, freeze, etc). Tracked
  // so we can clear them on scene shutdown. Without this, a combo-heavy
  // session leaks a closure per timeSlice call (and they fire often).
  private pendingTimers: Set<number> = new Set();

  private constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.zoomBaseScale = scene.cameras.main.zoom;
    this.ensureShockwaveTexture();
  }

  /**
   * Set the "baseline" time scale the system restores to after a freeze or slice.
   * Use this for persistent slow-mo (e.g. last-ball bullet time).
   */
  setBaseTimeScale(scale: number): void {
    this.baseTimeScale = scale;
    // Only force-apply if nothing else is currently overriding
    if (this.freezeRestoreTimer === undefined && this.scene.time.timeScale !== 0) {
      this.scene.time.timeScale = scale;
    }
  }

  getBaseTimeScale(): number {
    return this.baseTimeScale;
  }

  private ensureShockwaveTexture(): void {
    if (this.scene.textures.exists(this.shockwaveTexKey)) return;
    const g = this.scene.make.graphics({ x: 0, y: 0 }, false);
    // Ring with soft edges — two strokes, inner bright, outer fade
    g.lineStyle(6, 0xFFFFFF, 1);
    g.strokeCircle(64, 64, 60);
    g.lineStyle(2, 0xFFFFFF, 0.6);
    g.strokeCircle(64, 64, 56);
    g.generateTexture(this.shockwaveTexKey, 128, 128);
    g.destroy();
  }

  /**
   * Main entry — fire a named preset at a world position.
   * Returns immediately; all effects run async.
   */
  fire(x: number, y: number, preset: ImpactPresetName, tintColor = 0xFFFFFF): void {
    if (this.destroyed || !this.scene || !this.scene.sys || !this.scene.sys.isActive()) return;
    const p = IMPACT_PRESETS[preset];

    // 1. Hit-stop (ref-counted, uses real time)
    this.freeze(p.freezeMs);

    // 2. Camera kick + zoom punch
    this.cameraKick(p);

    // 3. Flash at impact point (additive)
    this.pointFlash(x, y, p, tintColor);

    // 4. Shockwave rings
    this.shockwave(x, y, p, tintColor);

    // 5. Optional barrel distort post-fx
    if (p.barrel) this.barrelPulse(p.barrel, p.zoomRecoverMs);
  }

  /**
   * Short alias — ball pop at world position.
   */
  pop(x: number, y: number, radius: number, tintColor = 0xFFFFFF): void {
    this.fire(x, y, ImpactSystem.presetForRadius(radius), tintColor);
  }

  /**
   * Boss hit — always LARGE with green tint.
   */
  bossHit(x: number, y: number): void {
    this.fire(x, y, 'LARGE', 0x44FF88);
  }

  /**
   * Boss phase change — HUGE cosmic moment.
   */
  bossPhase(x: number, y: number): void {
    this.fire(x, y, 'HUGE', 0xFF4466);
    this.timeSlice(500, 0.25);
  }

  /**
   * Boss death — COSMIC, gold, with long slow-mo.
   */
  bossDeath(x: number, y: number): void {
    this.fire(x, y, 'COSMIC', 0xFFD700);
    this.timeSlice(1200, 0.15);
  }

  /**
   * Final-blow cinematic — time slice + zoom in on the spot.
   * Used for the last ball of a stage or a boss killing blow.
   */
  finalBlow(x: number, y: number, durationMs = 900): void {
    if (this.destroyed) return;
    const cam = this.scene.cameras.main;

    // Save pre-blow state
    const preZoom = this.zoomBaseScale;
    const preScrollX = cam.scrollX;
    const preScrollY = cam.scrollY;

    // Aggressive slow-mo
    this.timeSlice(durationMs, 0.18);

    // Zoom and pan toward the impact (using pan for smoothness)
    const targetZoom = preZoom * 1.15;
    cam.zoomTo(targetZoom, durationMs * 0.4, 'Cubic.easeOut', true);

    // Camera drift toward the target point
    const screenCenterX = cam.width / 2;
    const screenCenterY = cam.height / 2;
    const panDx = (x - screenCenterX) * 0.25;
    const panDy = (y - screenCenterY) * 0.25;
    this.scene.tweens.add({
      targets: cam,
      scrollX: preScrollX + panDx,
      scrollY: preScrollY + panDy,
      duration: durationMs * 0.4,
      ease: 'Cubic.easeOut',
    });

    // Recovery
    this.scene.time.delayedCall(durationMs * 0.7, () => {
      if (this.destroyed) return;
      cam.zoomTo(preZoom, 400, 'Sine.easeInOut', true);
      this.scene.tweens.add({
        targets: cam,
        scrollX: preScrollX,
        scrollY: preScrollY,
        duration: 400,
        ease: 'Sine.easeInOut',
      });
    });
  }

  /**
   * Apply a time-slice slow-mo that automatically restores to the baseline.
   * Multiple calls stack — the most recent scale wins.
   */
  timeSlice(durationMs: number, scale = 0.3): void {
    if (this.destroyed) return;
    this.scene.time.timeScale = scale;
    const id = window.setTimeout(() => {
      this.pendingTimers.delete(id);
      if (this.destroyed) return;
      // Don't clobber an active freeze
      if (this.freezeRestoreTimer === undefined) {
        this.scene.time.timeScale = this.baseTimeScale;
      }
    }, durationMs);
    this.pendingTimers.add(id);
  }

  /**
   * Ref-counted hit-freeze — freeze the scene for `ms` real-time.
   * Safe under overlapping calls (deepest freeze extends the wall-clock duration).
   */
  freeze(ms: number): void {
    if (this.destroyed || ms <= 0) return;
    this.freezeRefCount++;
    this.scene.time.timeScale = 0;
    if (this.freezeRestoreTimer) {
      window.clearTimeout(this.freezeRestoreTimer);
      this.freezeRestoreTimer = undefined;
    }
    this.freezeRestoreTimer = window.setTimeout(() => {
      if (this.destroyed) return;
      this.freezeRefCount = 0;
      this.scene.time.timeScale = this.baseTimeScale;
      this.freezeRestoreTimer = undefined;
    }, ms);
  }

  /**
   * Full-screen color flash (wraps camera.flash with shake toggle).
   */
  screenFlash(color = 0xFFFFFF, duration = 200, force = false): void {
    if (this.destroyed) return;
    if (!force && !screenShakeState.isEnabled()) return;
    const r = (color >> 16) & 0xFF;
    const g = (color >> 8) & 0xFF;
    const b = color & 0xFF;
    this.scene.cameras.main.flash(duration, r, g, b);
  }

  // ─── Private ───────────────────────────────────────────────────────────────

  private cameraKick(p: ImpactPreset): void {
    if (!screenShakeState.isEnabled()) return;
    const cam = this.scene.cameras.main;

    // Shake
    cam.shake(p.shakeDuration, p.shakeIntensity);

    // Additive zoom punch that stacks
    this.cameraZoomStack++;
    const depth = Math.min(this.cameraZoomStack, 3);
    cam.zoomTo(this.zoomBaseScale * (1 + p.zoomPunch * depth), 60, 'Power2', true);
    this.scene.time.delayedCall(80, () => {
      if (this.destroyed) return;
      cam.zoomTo(this.zoomBaseScale, p.zoomRecoverMs, 'Back.easeOut', true);
      this.cameraZoomStack = Math.max(0, this.cameraZoomStack - 1);
    });
  }

  private pointFlash(x: number, y: number, p: ImpactPreset, color: number): void {
    const flash = this.scene.add.circle(x, y, 30, 0xFFFFFF, p.flashAlpha);
    flash.setBlendMode(Phaser.BlendModes.ADD);
    flash.setDepth(9999);
    // Tint toward impact color on the outer part
    const outer = this.scene.add.circle(x, y, 50, color, p.flashAlpha * 0.6);
    outer.setBlendMode(Phaser.BlendModes.ADD);
    outer.setDepth(9998);

    this.scene.tweens.add({
      targets: [flash, outer],
      scale: 3.5,
      alpha: 0,
      duration: 220,
      ease: 'Cubic.easeOut',
      onComplete: () => { flash.destroy(); outer.destroy(); },
    });
  }

  private shockwave(x: number, y: number, p: ImpactPreset, color: number): void {
    for (let i = 0; i < p.shockwaveRings; i++) {
      const delay = i * 70;
      const ring = this.scene.add.image(x, y, this.shockwaveTexKey);
      ring.setTint(color);
      ring.setBlendMode(Phaser.BlendModes.ADD);
      ring.setDepth(9000 + i);
      ring.setScale(0.15);
      ring.setAlpha(0);

      this.scene.tweens.add({
        targets: ring,
        alpha: { from: 0, to: 0.9 },
        duration: 80,
        delay,
      });
      this.scene.tweens.add({
        targets: ring,
        scale: p.shockwaveScale * (1 - i * 0.1),
        alpha: 0,
        duration: 520 - i * 40,
        delay: delay + 60,
        ease: 'Cubic.easeOut',
        onComplete: () => ring.destroy(),
      });
    }
  }

  private barrelPulse(strength: number, recoverMs: number): void {
    const cam = this.scene.cameras.main;
    if (!cam?.filters) return;

    try {
      if (!this.barrelFX) {
        this.barrelFX = cam.filters.internal.addBarrel(1);
      }
      if (this.barrelTween) this.barrelTween.stop();
      this.barrelFX.amount = 1 + strength;
      this.barrelTween = this.scene.tweens.add({
        targets: this.barrelFX,
        amount: 1,
        duration: recoverMs,
        ease: 'Cubic.easeOut',
      });
    } catch {
      // filter not ready — fail silently
    }
  }

  private destroy(): void {
    this.destroyed = true;
    if (this.freezeRestoreTimer) window.clearTimeout(this.freezeRestoreTimer);
    // Clear any pending wall-clock timers owned by this system so their
    // closures don't keep the scene alive after shutdown.
    for (const id of this.pendingTimers) {
      try { window.clearTimeout(id); } catch {}
    }
    this.pendingTimers.clear();
    if (this.barrelTween) this.barrelTween.stop();
    if (this.barrelFX && this.scene?.cameras?.main?.filters) {
      try { this.scene.cameras.main.filters.internal.remove(this.barrelFX); } catch {}
    }
    this.barrelFX = undefined;
    this.barrelTween = undefined;
  }
}
